/**
 * Potansiyel müşteriler (v0.1.90): the pure parts of the page. Grouping (kind, production,
 * stage, market, source, found day, decision), the filters and the sort Sercan picks, and the
 * Excel export (UTF-8 with BOM, ';' separated, Turkish headers, the rows on screen).
 */

import {
  COUNTRY_LABELS_TR,
  DECIDER_LABELS_TR,
  KIND_LABELS_TR,
  KIND_ORDER,
  PRODUCTION_LABELS_TR,
  PRODUCTION_ORDER,
  PROVIDER_LABELS_TR,
  type ProspectKind,
  type ProspectRow,
  STAGE_LABELS_TR,
  STAGE_ORDER,
  UNFIT_LABELS_TR,
} from '../api/prospects.js';
import { formatClock, formatIsoDay, istanbulIsoDate } from '../lib/format.js';

export const GROUP_DIMS = ['kind', 'production', 'stage', 'market', 'source', 'day', 'decision'] as const;
export type GroupDim = (typeof GROUP_DIMS)[number];

export const GROUP_DIM_LABELS_TR: Readonly<Record<GroupDim, string>> = {
  kind: 'Tür',
  production: 'Üretim',
  stage: 'Aşama',
  market: 'Pazar',
  source: 'Kaynak',
  day: 'Bulunduğu gün',
  decision: 'Karar',
};

export const MAIL_FILTERS = ['all', 'drafted', 'sent', 'not_sent', 'has_email', 'no_email', 'replied'] as const;
export type MailFilter = (typeof MAIL_FILTERS)[number];

export const MAIL_FILTER_LABELS_TR: Readonly<Record<MailFilter, string>> = {
  all: 'Hepsi',
  drafted: 'Mail hazır',
  sent: 'Mail gitti',
  not_sent: 'Mail gitmedi',
  has_email: 'E-postası var, mail gitmedi',
  no_email: 'E-postası yok',
  replied: 'Cevap verdi',
};

export const SORT_KEYS = ['fit', 'found', 'decision', 'activity', 'name'] as const;
export type SortKey = (typeof SORT_KEYS)[number];

export const SORT_LABELS_TR: Readonly<Record<SortKey, string>> = {
  fit: 'Uyum puanı (yüksekten)',
  found: 'En yeni bulunan',
  decision: 'En son karar',
  activity: 'En son hareket',
  name: 'Ada göre (A-Z)',
};

export interface ProspectFilters {
  q: string;
  /** One picked value per group dimension (the chip or the select); missing means all. */
  picks: Partial<Record<GroupDim, string>>;
  mail: MailFilter;
  fitMin: number;
  sort: SortKey;
}

export const EMPTY_FILTERS: ProspectFilters = { q: '', picks: {}, mail: 'all', fitMin: 0, sort: 'fit' };

export const NO_DECISION = 'none';

export function sourceKey(row: ProspectRow): string {
  return row.found.provider ?? row.source;
}

export function groupKey(row: ProspectRow, dim: GroupDim): string {
  switch (dim) {
    case 'kind':
      return row.kind;
    case 'production':
      return row.production;
    case 'stage':
      return row.stage;
    case 'market':
      return `${row.country ?? '??'}${row.state ? `|${row.state}` : ''}`;
    case 'source':
      return sourceKey(row);
    case 'day': {
      const at = row.found.at ?? row.firstSeenAt;
      return at === null ? '' : istanbulIsoDate(at);
    }
    case 'decision':
      return row.decision?.verdict ?? NO_DECISION;
  }
}

export function groupLabel(dim: GroupDim, key: string, sample?: ProspectRow): string {
  switch (dim) {
    case 'kind':
      return KIND_LABELS_TR[key as ProspectKind] ?? key;
    case 'production':
      return PRODUCTION_LABELS_TR[key as keyof typeof PRODUCTION_LABELS_TR] ?? key;
    case 'stage':
      return STAGE_LABELS_TR[key] ?? key;
    case 'market': {
      const [country, state] = key.split('|');
      const name = country === '??' || country === undefined ? 'Ülke yok' : (COUNTRY_LABELS_TR[country] ?? country);
      return state ? `${name} · ${state}` : name;
    }
    case 'source':
      return PROVIDER_LABELS_TR[key] ?? key;
    case 'day':
      return key === '' ? 'Tarih yok' : formatIsoDay(key);
    case 'decision':
      return key === NO_DECISION ? 'Henüz karar yok' : (sample?.decision?.verdictLabel ?? key);
  }
}

const QUALIFIED = new Set(['nitelikli', 'dogrulandi', 'temas', 'cevap', 'musteri']);

export function isQualified(row: ProspectRow): boolean {
  return QUALIFIED.has(row.stage);
}

function haystack(row: ProspectRow): string {
  return [
    row.name,
    row.legalName,
    row.domain,
    row.locality,
    row.state,
    row.postcode,
    row.country,
    row.kindLabel,
    row.productionLabel,
    row.makes,
    row.why,
    row.found.query,
    row.found.locality,
    row.registryId,
    row.decision?.gate,
    ...(row.decision?.reasons ?? []),
  ]
    .filter((part): part is string => typeof part === 'string')
    .join(' ')
    .toLocaleLowerCase('tr');
}

function mailMatches(row: ProspectRow, mail: MailFilter): boolean {
  switch (mail) {
    case 'all':
      return true;
    case 'drafted':
      return (row.contact.drafts ?? 0) > 0;
    case 'sent':
      return row.contact.sent > 0;
    case 'not_sent':
      return row.contact.sent === 0;
    case 'has_email':
      return row.contact.emails > 0 && row.contact.sent === 0;
    case 'no_email':
      return row.contact.emails === 0;
    case 'replied':
      return row.contact.lastReplyAt !== null;
  }
}

/** The rows that pass every filter except the pick of `except` (the group counts use this). */
export function filterProspects(
  rows: readonly ProspectRow[],
  filters: ProspectFilters,
  except?: GroupDim,
): ProspectRow[] {
  const needle = filters.q.trim().toLocaleLowerCase('tr');
  const picks = Object.entries(filters.picks).filter(
    (entry): entry is [GroupDim, string] => entry[1] !== undefined && entry[0] !== except,
  );
  return rows.filter(
    (row) =>
      row.fit.total >= filters.fitMin &&
      mailMatches(row, filters.mail) &&
      picks.every(([dim, key]) => groupKey(row, dim) === key) &&
      (needle === '' || haystack(row).includes(needle)),
  );
}

function newest(a: number | null, b: number | null): number {
  return (b ?? 0) - (a ?? 0);
}

export function sortProspects(rows: readonly ProspectRow[], sort: SortKey): ProspectRow[] {
  const out = [...rows];
  const byName = (a: ProspectRow, b: ProspectRow) => a.name.localeCompare(b.name, 'tr');
  switch (sort) {
    case 'fit':
      return out.sort((a, b) => b.fit.total - a.fit.total || byName(a, b));
    case 'found':
      return out.sort((a, b) => newest(a.found.at ?? a.firstSeenAt, b.found.at ?? b.firstSeenAt) || byName(a, b));
    case 'decision':
      return out.sort((a, b) => newest(a.decision?.at ?? null, b.decision?.at ?? null) || byName(a, b));
    case 'activity':
      return out.sort((a, b) => newest(a.lastActivityAt, b.lastActivityAt) || byName(a, b));
    case 'name':
      return out.sort(byName);
  }
}

export interface ProspectGroup {
  key: string;
  label: string;
  count: number;
  qualified: number;
  withEmail: number;
  mailed: number;
  avgFit: number;
}

function fixedOrder(dim: GroupDim): readonly string[] | null {
  if (dim === 'kind') return KIND_ORDER;
  if (dim === 'production') return PRODUCTION_ORDER;
  if (dim === 'stage') return STAGE_ORDER;
  return null;
}

export function groupProspects(rows: readonly ProspectRow[], dim: GroupDim): ProspectGroup[] {
  const groups = new Map<string, { sample: ProspectRow; rows: ProspectRow[] }>();
  for (const row of rows) {
    const key = groupKey(row, dim);
    const group = groups.get(key);
    if (group === undefined) groups.set(key, { sample: row, rows: [row] });
    else group.rows.push(row);
  }
  const out: ProspectGroup[] = [...groups.entries()].map(([key, group]) => ({
    key,
    label: groupLabel(dim, key, group.sample),
    count: group.rows.length,
    qualified: group.rows.filter(isQualified).length,
    withEmail: group.rows.filter((row) => row.contact.emails > 0).length,
    mailed: group.rows.filter((row) => row.contact.sent > 0).length,
    avgFit: Math.round(group.rows.reduce((sum, row) => sum + row.fit.total, 0) / group.rows.length),
  }));
  const order = fixedOrder(dim);
  if (order !== null) {
    const rank = (key: string) => {
      const index = order.indexOf(key);
      return index < 0 ? order.length : index;
    };
    return out.sort((a, b) => rank(a.key) - rank(b.key) || b.count - a.count);
  }
  if (dim === 'day') return out.sort((a, b) => b.key.localeCompare(a.key));
  return out.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'tr'));
}

/** "2026-09-30 14:03" in Istanbul time; empty for no instant. */
export function csvInstant(at: number | null): string {
  return at === null ? '' : `${istanbulIsoDate(at)} ${formatClock(at)}`;
}

function csvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  let text = String(value);
  // A cell that starts like a formula is kept as text (Excel would run it otherwise).
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[";\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const CSV_COLUMNS: readonly [string, (row: ProspectRow) => string | number | null][] = [
  ['Firma', (row) => row.name],
  ['Yasal ad', (row) => row.legalName],
  ['Web sitesi', (row) => row.website],
  ['Alan adı', (row) => row.domain],
  ['Ülke', (row) => (row.country === null ? null : (COUNTRY_LABELS_TR[row.country] ?? row.country))],
  ['Eyalet', (row) => row.state],
  [
    'Eyalet dayanağı',
    (row) => {
      const basis =
        row.stateBasis === 'scan'
          ? 'tarama bölgesi (aday)'
          : row.stateBasis === 'site'
            ? 'adres'
            : row.stateBasis === 'registry'
              ? 'ABN sicili'
              : row.stateBasis === 'name'
                ? 'firma adından (aday)'
                : null;
      return basis !== null && row.locationConflict === true ? `${basis} (konum çelişkili)` : basis;
    },
  ],
  ['Yer', (row) => row.locality],
  ['Posta kodu', (row) => row.postcode],
  ['Adres', (row) => row.address],
  ['Tür', (row) => row.kindLabel],
  [
    'Tür dayanağı',
    (row) =>
      row.kindBasis === 'pilot'
        ? 'pilot notu'
        : row.kindBasis === 'gate'
          ? 'kapının rol kararı'
          : `tahmin${row.kindMatched ? ` (${row.kindMatched})` : ''}`,
  ],
  ['Üretim', (row) => row.productionLabel],
  [
    'Üretim dayanağı',
    (row) =>
      row.productionBasis === 'pilot'
        ? 'pilot notu'
        : row.productionBasis === 'gate'
          ? 'kapının rol kararı'
          : 'türden tahmin',
  ],
  ['Kapının rol kararı', (row) => (row.role && row.role !== 'unknown' ? (row.roleLabel ?? row.role) : null)],
  ['Neden potansiyel', (row) => row.why],
  ['Ne yapıyor', (row) => row.makes],
  ['Aşama', (row) => STAGE_LABELS_TR[row.stage] ?? row.stage],
  ['Aşama notu', (row) => row.stageNote ?? null],
  ['Uyum puanı', (row) => row.fit.total],
  ['Araç (40)', (row) => row.fit.vehicle],
  ['Pazar (20)', (row) => row.fit.market],
  ['Tasarım (20)', (row) => row.fit.design],
  ['Büyüklük (10)', (row) => row.fit.size],
  ['Boşluk (10)', (row) => row.fit.gap],
  ['Güven', (row) => row.confidence],
  ['Bulunma tarihi', (row) => csvInstant(row.found.at ?? row.firstSeenAt)],
  ['Kaynak', (row) => PROVIDER_LABELS_TR[sourceKey(row)] ?? sourceKey(row)],
  ['Arama', (row) => row.found.query],
  ['Arama yeri', (row) => row.found.locality],
  ['Hücre', (row) => row.found.cellId],
  ['Kaç aramada çıktı', (row) => row.hits],
  ['Son karar', (row) => row.decision?.verdictLabel ?? 'Henüz karar yok'],
  ['Karar tarihi', (row) => csvInstant(row.decision?.at ?? null)],
  ['Karar veren', (row) => (row.decision?.by ? (DECIDER_LABELS_TR[row.decision.by] ?? row.decision.by) : null)],
  ['Kapı', (row) => row.decision?.gate ?? null],
  ['Nedenler', (row) => (row.decision === null ? null : row.decision.reasons.join(' | '))],
  [
    'Uygun değil nedeni',
    (row) => (row.unfitReason === null ? null : (UNFIT_LABELS_TR[row.unfitReason] ?? row.unfitReason)),
  ],
  ['E-posta sayısı', (row) => row.contact.emails],
  ['Telefon sayısı', (row) => row.contact.phones],
  ['E-posta adresleri', (row) => row.contact.emailList ?? null],
  ['İletişim sayfası', (row) => row.contact.contactPage ?? null],
  ['Hazır mail', (row) => row.contact.drafts ?? 0],
  ['Gönderilen mail', (row) => row.contact.sent],
  ['Son mail', (row) => csvInstant(row.contact.lastSentAt)],
  ['Son cevap', (row) => csvInstant(row.contact.lastReplyAt)],
  ['Sicil', (row) => row.registry],
  ['Sicil no', (row) => row.registryId],
  ['Lead no', (row) => row.leadId],
];

/** The Excel file: BOM, Turkish headers, ';' between cells (Excel's separator in Turkish Windows). */
export function prospectsCsv(rows: readonly ProspectRow[]): string {
  const lines = [CSV_COLUMNS.map(([header]) => csvCell(header)).join(';')];
  for (const row of rows) lines.push(CSV_COLUMNS.map(([, value]) => csvCell(value(row))).join(';'));
  return `﻿${lines.join('\r\n')}\r\n`;
}
